<template>
  <div class="vt-node">
    <!-- Trilho -->
    <div class="vt-rail">
      <div class="vt-circle" :style="{ borderColor: ringColor }">
        <span class="vt-avatar">{{ iniciais }}</span>
      </div>
      <div v-if="!isLast" class="vt-line"></div>
    </div>

    <!-- Conteúdo -->
    <div class="vt-content">
      <!-- Cabeçalho narrativo -->
      <div class="vt-head">
        <div class="vt-head-left">
          <span class="vt-badge" :class="'vt-badge--' + kind">{{ acaoLabel }}</span>
          <span class="vt-narr">
            <strong>{{ colaboradorNome }}</strong>
            {{ verbo }} {{ nCampos }} {{ nCampos === 1 ? 'campo' : 'campos' }}
          </span>
        </div>
        <span class="vt-time">{{ dataHora }}</span>
      </div>

      <!-- Bloco de alterações -->
      <div v-if="campos.length" class="vt-changes">
        <div v-for="campo in campos" :key="campo.key" class="vt-change">
          <span class="vt-field">{{ campo.label }}</span>

          <span v-if="campo.isSql" class="vt-vals vt-vals--sql">
            <button
              v-if="campo.temAntigo"
              type="button"
              class="vt-versql"
              @click="$emit('ver-sql', { sql: campo.antigoRaw, titulo: campo.label + ' — anterior' })"
            >
              <fa-icon icon="database" /> Ver SQL anterior
            </button>
            <span v-if="campo.temAntigo && campo.temNovo" class="vt-arrow">→</span>
            <button
              v-if="campo.temNovo"
              type="button"
              class="vt-versql"
              @click="$emit('ver-sql', { sql: campo.novoRaw, titulo: campo.label + ' — novo' })"
            >
              <fa-icon icon="database" /> Ver SQL novo
            </button>
            <button
              v-if="campo.temAntigo && campo.temNovo"
              type="button"
              class="vt-versql vt-versql--diff"
              @click="$emit('ver-diff', { old: campo.antigoRaw, new: campo.novoRaw, titulo: campo.label })"
            >
              <fa-icon icon="exchange-alt" /> Ver DIFF
            </button>
          </span>

          <span v-else class="vt-vals">
            <span class="vt-old">{{ campo.antigo }}</span>
            <span class="vt-arrow">→</span>
            <span class="vt-new">{{ campo.novo }}</span>
          </span>
        </div>
      </div>

      <!-- Rodapé: motivo + restaurar -->
      <div class="vt-foot">
        <div class="vt-motivo">
          <template v-if="temMotivo">
            <span class="vt-chip">
              <fa-icon icon="ticket-alt" /> {{ chipLabel }}
            </span>
            <span class="vt-motivo-text">
              <template v-if="motivoExtra">{{ motivoExtra }} · </template>
              por <strong>{{ item.motivo_alterado_por || '—' }}</strong>
              <em v-if="item.motivo_descricao"> "{{ item.motivo_descricao }}"</em>
            </span>
          </template>
          <span v-else class="vt-no-motivo">— sem motivo registrado</span>
        </div>

        <button
          v-if="podeRestaurar"
          type="button"
          class="vt-restore"
          title="Carregar esta versão no editor para revisão"
          @click="$emit('restaurar', restaurarSql)"
        >
          <fa-icon icon="arrow-left" /> Restaurar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { toDateTime } from "@/helpers/utils";

// Metadados setados automaticamente em todo save — não contam como alteração
// e não são exibidos como linhas (a data/hora já aparece no cabeçalho do nó).
const META_FIELDS = ["datahora_atualizacao", "colaborador_id_atualizacao"];

const LABELS = {
  datahora_atualizacao: "Data/hora de atualização",
  datahora_criacao: "Data/hora de criação",
  sql: "Consulta SQL",
  descricao: "Descrição",
  status: "Status",
  cupom: "Cupom",
  view_name: "View",
  ativo: "Ativo",
  banco: "Banco",
  tipo: "Tipo",
  colaborador_id_atualizacao: "Colaborador",
  nome: "Nome",
};

export default {
  name: "LogAlteracaoItem",
  props: {
    item: { type: Object, required: true },
    isLast: { type: Boolean, default: false },
    restorable: { type: Boolean, default: false },
    fieldLabels: { type: Object, default: () => ({}) },
  },
  computed: {
    labels() {
      return { ...LABELS, ...this.fieldLabels };
    },
    colaboradorNome() {
      return (this.item.colaborador && this.item.colaborador.nome) || "—";
    },
    iniciais() {
      const partes = String(this.colaboradorNome).trim().split(/\s+/);
      if (!partes[0] || partes[0] === "—") return "?";
      const a = partes[0][0] || "";
      const b = partes.length > 1 ? partes[partes.length - 1][0] : (partes[0][1] || "");
      return (a + b).toUpperCase();
    },
    kind() {
      const a = String(this.item.acao || "").toUpperCase();
      if (a === "INSERT" || a === "CREATE") return "insert";
      if (a === "DELETE") return "delete";
      if (a === "UPDATE") return "update";
      return "other";
    },
    acaoLabel() {
      return { insert: "INSERT", delete: "DELETE", update: "UPDATE" }[this.kind]
        || String(this.item.acao || "—").toUpperCase();
    },
    verbo() {
      return { insert: "criou", delete: "removeu", update: "editou" }[this.kind] || "alterou";
    },
    ringColor() {
      // Paleta de status da feature (tokens no parcial _cargas.scss); os hex
      // são fallback para contextos sem as custom properties carregadas.
      return {
        insert: "var(--verso-sucesso, #2a8a52)",
        delete: "var(--verso-falha, #c0392b)",
        update: "var(--verso-alerta, #c1751f)",
      }[this.kind] || "var(--primary, #3a3e81)";
    },
    dataHora() {
      return toDateTime(this.item.datahora_criacao);
    },
    campos() {
      const a = this.item.dados_anteriores || {};
      const n = this.item.dados_novos || {};
      const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(n)]))
        .filter((k) => !META_FIELDS.includes(k));

      return keys.map((key) => {
        const isSql = String(key).toLowerCase() === "sql";
        const temAntigo = this.preenchido(a[key]);
        const temNovo = this.preenchido(n[key]);
        return {
          key,
          label: this.labels[key] || key,
          isSql,
          antigoRaw: a[key],
          novoRaw: n[key],
          antigo: temAntigo ? this.formatar(a[key]) : "—",
          novo: temNovo ? this.formatar(n[key]) : "—",
          temAntigo,
          temNovo,
        };
      });
    },
    nCampos() {
      return this.campos.length;
    },
    temMotivo() {
      return !!this.item.motivo_tipo;
    },
    chipLabel() {
      if (this.item.motivo_tipo === "ticket") {
        return "# " + (this.item.motivo_ticket || "—");
      }
      if (this.item.motivo_tipo === "cliente_implantacao") {
        return "Implantação";
      }
      return this.item.motivo_tipo || "—";
    },
    motivoExtra() {
      if (this.item.motivo_tipo !== "cliente_implantacao") return "";
      const partes = [];
      if (this.item.motivo_etapa) partes.push("etapa: " + this.item.motivo_etapa);
      if (this.item.motivo_solicitante) partes.push("solicitante: " + this.item.motivo_solicitante);
      return partes.join(" · ");
    },
    restaurarSql() {
      const n = this.item.dados_novos || {};
      const a = this.item.dados_anteriores || {};
      return n.sql || a.sql || null;
    },
    podeRestaurar() {
      return this.restorable && !!this.restaurarSql;
    },
  },
  methods: {
    preenchido(v) {
      return v !== null && v !== undefined && v !== "";
    },
    formatar(v) {
      if (typeof v === "boolean") return v ? "Sim" : "Não";
      if (v !== null && typeof v === "object") {
        try {
          return JSON.stringify(v);
        } catch (_) {
          return String(v);
        }
      }
      return String(v);
    },
  },
};
</script>

<style scoped>
.vt-node {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 16px;
}

/* Trilho */
.vt-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.vt-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid var(--vt-navy, #3a3e81);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.vt-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--vt-navy, #3a3e81);
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: .02em;
}

.vt-line {
  flex: 1;
  width: 2px;
  background: var(--vt-border, #e4e7ec);
  margin-top: 4px;
  min-height: 18px;
}

/* Conteúdo */
.vt-content {
  padding-bottom: 22px;
  min-width: 0;
}

.vt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.vt-head-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.vt-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: .04em;
  border: 1px solid;
}

.vt-badge--update { background: #fff3e0; color: #c1751f; border-color: #f3ddb8; }
.vt-badge--insert { background: #e7f6ec; color: #2a8a52; border-color: #c6e9d3; }
.vt-badge--delete { background: #fdeaea; color: #c0392b; border-color: #f4cccc; }
.vt-badge--other  { background: #eef0f3; color: #5a6072; border-color: #e4e7ec; }

.vt-narr {
  font-size: 13.5px;
  color: var(--vt-text, #2c2e38);
}

.vt-time {
  font-size: 12.5px;
  color: var(--vt-muted, #949aa8);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Bloco de alterações */
.vt-changes {
  background: var(--vt-head, #f6f7f9);
  border: 1px solid var(--vt-border, #e4e7ec);
  border-radius: 8px;
  padding: 11px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.vt-change {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 14px;
  align-items: baseline;
  font-size: 13px;
}

.vt-field {
  color: var(--vt-text-soft, #5a6072);
  font-weight: 600;
}

.vt-vals {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 12px;
  min-width: 0;
}

.vt-old { color: #b06a4f; word-break: break-word; }
.vt-new { color: #2a7a4a; word-break: break-word; }
.vt-arrow { color: var(--vt-muted, #949aa8); }

.vt-versql {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #edf8f9;
  color: var(--vt-teal-dark, #1a7a83);
  border: 1px solid #c5e6e9;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.vt-versql:hover { background: #dcf0f3; }

.vt-versql--diff {
  background: #eef0f3;
  color: #3a3e81;
  border-color: #d3d8e0;
}

.vt-versql--diff:hover { background: #e2e6ec; }

/* Rodapé */
.vt-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.vt-motivo {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: var(--vt-text-soft, #5a6072);
  min-width: 0;
}

.vt-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--vt-teal, #2298a3);
  color: #fff;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.vt-motivo-text em { color: var(--vt-text-soft, #5a6072); }

.vt-no-motivo {
  color: var(--vt-muted, #949aa8);
  font-style: italic;
  font-size: 12.5px;
}

.vt-restore {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: var(--vt-navy, #3a3e81);
  border: 1px solid var(--vt-navy, #3a3e81);
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}

.vt-restore:hover {
  background: var(--vt-navy, #3a3e81);
  color: #fff;
}

@media (max-width: 767.98px) {
  .vt-change {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
